import { useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type FxDirectionInfo,
  type FxOrderView,
  type FxQuote,
  type FxResponse,
} from '../api';
import { PinPad } from '../components/PinPad';
import { CopyText, Empty, Field, Header, Notice, Rows, Segmented } from '../components/ui';
import { alertUser, haptic } from '../telegram';

type Direction = FxDirectionInfo['direction'];

const PAYOUT_LABEL: Record<string, string> = {
  alipay_account: '支付宝账号',
  alipay_qr: '支付宝收款码',
  wechat_qr: '微信收款码',
  bank_usd: '美元银行账户',
  wallet_credit: '钱包余额',
};

function readQrFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });
}

export function FxPage({ initialDirection }: { initialDirection?: Direction }) {
  const [data, setData] = useState<FxResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [direction, setDirection] = useState<Direction>(initialDirection ?? 'U2CNY');
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<FxQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [method, setMethod] = useState<string>('');
  const [name, setName] = useState('');
  const [account, setAccount] = useState('');
  const [bank, setBank] = useState('');
  const [swift, setSwift] = useState('');
  const [qr, setQr] = useState<string>('');
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState<FxOrderView[] | null>(null);
  const [showOrders, setShowOrders] = useState(false);

  const load = (): void => {
    api
      .fx()
      .then(setData)
      .catch((err: ApiError) => setLoadError(err.message));
  };

  useEffect(load, []);

  // Rates move: refresh the board (and the live quote) every 10s.
  useEffect(() => {
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, []);

  const info = useMemo(
    () => data?.directions.find((item) => item.direction === direction) ?? null,
    [data, direction],
  );

  useEffect(() => {
    if (!info) return;
    setMethod((current) =>
      current && info.payoutMethods.includes(current) ? current : (info.payoutMethods[0] ?? ''),
    );
  }, [info]);

  useEffect(() => {
    if (!info?.enabled || !amount || Number(amount) <= 0) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    let cancelled = false;
    api
      .fxQuote({ direction, amount })
      .then((result) => {
        if (!cancelled) {
          setQuote(result);
          setQuoteError(null);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [direction, amount, info?.enabled, info?.rate]);

  useEffect(() => {
    if (showOrders && orders === null) {
      api
        .fxOrders()
        .then((res) => setOrders(res.items))
        .catch((err: ApiError) => alertUser(err.message));
    }
  }, [showOrders, orders]);

  const payoutPayload = (): Record<string, string> | undefined => {
    if (!info || info.side === 'buy') return undefined;
    if (method === 'alipay_account') return { method, name, account };
    if (method === 'bank_usd') return { method, name, account, bank, ...(swift ? { swift } : {}) };
    if (method === 'alipay_qr' || method === 'wechat_qr') return { method, qrFileId: qr };
    return undefined;
  };

  const pinRequired = Boolean(
    data && quote && info?.side === 'sell' && quote.usdtAmount > data.pinFreeLimit,
  );

  const submit = async (pin?: string): Promise<void> => {
    if (!info || !quote) return;
    setBusy(true);
    // Keep the request separate from the post-success UI work so a rendering
    // or Telegram SDK hiccup is never reported as a failed order.
    let result: Awaited<ReturnType<typeof api.fxOrder>>;
    try {
      result = await api.fxOrder({
        direction,
        amount,
        payout: payoutPayload(),
        ...(pin ? { pin } : {}),
      });
    } catch (err) {
      const apiError = err as ApiError;
      haptic('error');
      if (
        apiError.code === 'PIN_WRONG' ||
        apiError.code === 'PIN_REQUIRED' ||
        apiError.code === 'PIN_INVALID_FORMAT'
      ) {
        setPinOpen(true);
        setPinError(apiError.message);
      } else {
        setPinOpen(false);
        setPinError(null);
        alertUser(apiError.message);
      }
      setBusy(false);
      return;
    }

    setBusy(false);
    setPinOpen(false);
    setPinError(null);
    setAmount('');
    setQuote(null);
    setOrders(null);
    load();
    haptic('success');
    const payinText = result.payin
      ? `\n请向以下账户付款：\n${Object.entries(result.payin)
          .filter(([, value]) => Boolean(value))
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n')}`
      : '';
    alertUser(`换汇申请已提交\n单号：${result.id}\n状态：${result.statusText}${payinText}`);
  };

  const onConfirm = (): void => {
    if (!info || !quote) return;
    if (pinRequired) {
      setPinError(null);
      setPinOpen(true);
      return;
    }
    void submit();
  };

  if (loadError) {
    return (
      <div className="app">
        <Header title="换汇" />
        <div className="body">
          <div className="error">{loadError}</div>
        </div>
      </div>
    );
  }

  if (!data || !info) {
    return (
      <div className="app">
        <Header title="换汇" />
        <div className="loading">加载中…</div>
      </div>
    );
  }

  if (showOrders) {
    return (
      <div className="app">
        <Header title="换汇记录" onBack={() => setShowOrders(false)} />
        <div className="body">
          {orders === null ? (
            <div className="loading">加载中…</div>
          ) : orders.length === 0 ? (
            <Empty text="暂时没有数据哦~" />
          ) : (
            <div className="list">
              {orders.map((order) => (
                <div className="item" key={order.id}>
                  <div className="top">
                    <b>{order.label}</b>
                    <span className={`badge ${order.status}`}>{order.statusText}</span>
                  </div>
                  <div className="meta">
                    {order.usdtAmountText} USDT ↔ {order.fiatAmountText} {order.fiatCurrency} · 汇率{' '}
                    {order.rate}
                  </div>
                  <div className="meta mono">单号 {order.id}</div>
                  {order.side === 'buy' && order.status === 'pending' ? (
                    <button
                      type="button"
                      className="ghost"
                      style={{ marginTop: 8 }}
                      onClick={() =>
                        void api
                          .fxMarkPaid(order.id)
                          .then(() => {
                            setOrders(null);
                            alertUser('已通知客服核对，请等待入账');
                          })
                          .catch((err: ApiError) => alertUser(err.message))
                      }
                    >
                      我已付款
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const amountLabel =
    info.side === 'sell' ? '兑出金额（USDT）' : `兑出金额（${info.fiatCurrency}）`;
  const amountSuffix = info.side === 'sell' ? 'USDT' : info.fiatCurrency;

  const payoutFormReady =
    info.side === 'buy' ||
    (method === 'alipay_account' && name.trim() !== '' && account.trim() !== '') ||
    (method === 'bank_usd' && name.trim() !== '' && account.trim() !== '' && bank.trim() !== '') ||
    ((method === 'alipay_qr' || method === 'wechat_qr') && qr !== '');

  return (
    <div className="app">
      <Header
        title="换汇"
        action={
          <span className="link" onClick={() => setShowOrders(true)}>
            记录
          </span>
        }
      />
      <div className="body">
        <div>
          <span className="section-title">换汇方向</span>
          <div className="dir-grid">
            {data.directions.map((item) => (
              <button
                key={item.direction}
                type="button"
                disabled={!item.enabled}
                className={item.direction === direction ? 'active' : ''}
                onClick={() => {
                  setDirection(item.direction);
                  setAmount('');
                  setQuote(null);
                }}
              >
                {item.label}
                <span className="rate">
                  {item.enabled ? (item.rateText ?? '') : (item.disabledReason ?? '暂未开通')}
                </span>
              </button>
            ))}
          </div>
        </div>

        {info.enabled ? (
          <>
            <div className="hint">
              <span>{info.rateText}</span>
              <span>每 10 秒刷新</span>
            </div>

            <Field label={amountLabel}>
              <input
                inputMode="decimal"
                placeholder="请输入换汇金额"
                value={amount}
                onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ''))}
              />
              <span className="suffix">{amountSuffix}</span>
              {info.side === 'sell' ? (
                <span
                  className="suffix action"
                  onClick={() => setAmount(String(data.balance / 1e6))}
                >
                  全部
                </span>
              ) : null}
            </Field>

            <Rows
              items={[
                ['汇率', info.rate ?? '-'],
                ['手续费', quote ? `${quote.feeUsdtText} USDT` : '-'],
                ['预计到账', quote ? quote.receiveText : '-'],
                ['单笔最低', `${info.minText} USDT`],
              ]}
            />
            {quoteError ? <div className="error">{quoteError}</div> : null}

            {info.side === 'sell' ? (
              <>
                <div>
                  <span className="section-title">收款方式</span>
                  <Segmented
                    options={info.payoutMethods.map((key) => ({
                      key,
                      label: PAYOUT_LABEL[key] ?? key,
                    }))}
                    active={method}
                    onChange={setMethod}
                  />
                </div>

                {method === 'alipay_account' ? (
                  <>
                    <Field label="收款人姓名">
                      <input
                        placeholder="请输入收款人姓名"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </Field>
                    <Field label="支付宝账号">
                      <input
                        placeholder="请输入支付宝账号"
                        value={account}
                        onChange={(event) => setAccount(event.target.value)}
                      />
                    </Field>
                  </>
                ) : null}

                {method === 'bank_usd' ? (
                  <>
                    <Field label="收款人姓名">
                      <input
                        placeholder="请输入收款人姓名"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </Field>
                    <Field label="银行账号">
                      <input
                        className="mono"
                        placeholder="请输入银行账号"
                        value={account}
                        onChange={(event) => setAccount(event.target.value)}
                      />
                    </Field>
                    <Field label="开户银行">
                      <input
                        placeholder="请输入开户银行"
                        value={bank}
                        onChange={(event) => setBank(event.target.value)}
                      />
                    </Field>
                    <Field label="SWIFT（可选）">
                      <input
                        className="mono"
                        placeholder="选填"
                        value={swift}
                        onChange={(event) => setSwift(event.target.value)}
                      />
                    </Field>
                  </>
                ) : null}

                {method === 'alipay_qr' || method === 'wechat_qr' ? (
                  <Field label={PAYOUT_LABEL[method] as string} hint={<span>请上传清晰的收款码截图</span>}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        if (file.size > 300 * 1024) {
                          alertUser('图片过大，请压缩到 300KB 以内');
                          return;
                        }
                        void readQrFile(file).then(setQr);
                      }}
                    />
                    {qr ? <span className="suffix">已选择</span> : null}
                  </Field>
                ) : null}
              </>
            ) : (
              <>
                {info.payin ? (
                  <div className="rows">
                    {Object.entries(info.payin)
                      .filter(([, value]) => Boolean(value))
                      .map(([key, value]) => (
                        <div className="row" key={key}>
                          <span>{key}</span>
                          <CopyText value={String(value)} />
                        </div>
                      ))}
                  </div>
                ) : null}
                <Notice>付款后请在「记录」中点击「我已付款」，客服核对后自动入账。</Notice>
              </>
            )}

            {pinRequired ? <div className="hint">超过免密额度，提交前需验证安全密码</div> : null}

            <button
              type="button"
              className="primary"
              disabled={!quote || busy || !payoutFormReady}
              onClick={onConfirm}
            >
              确认换汇
            </button>
          </>
        ) : (
          <Empty text={info.disabledReason ?? '暂未开通'} />
        )}
      </div>

      {pinOpen ? (
        <PinPad
          title="请输入安全密码"
          subtitle="超过免密额度的换汇需验证"
          error={pinError}
          onSubmit={(pin) => void submit(pin)}
          onCancel={() => {
            setPinOpen(false);
            setPinError(null);
          }}
        />
      ) : null}
    </div>
  );
}
